import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  TrendingUp, Wallet, Building2, PercentCircle, Users, Home,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtPct } from "@/lib/format";
import { Card } from "@/components/ui/card";
import Onboarding from "@/components/Onboarding";

const PIE_COLORS = ["#10B981", "#F59E0B", "#3B82F6", "#EF4444", "#8B5CF6", "#EC4899"];

function KpiCard({ icon: Icon, label, value, accent, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className="p-6 rounded-sm border-border hover:-translate-y-1 transition-transform duration-300 h-full">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icon className="h-4 w-4" style={{ color: accent }} />
        </div>
        <p className="font-display text-3xl tabular-nums">{value}</p>
      </Card>
    </motion.div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading || !data)
    return <div className="h-64 flex items-center justify-center text-muted-foreground">…</div>;

  const empty = data.properties_count === 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-4xl">{t("welcome")}, {user?.name?.split(" ")[0]}</h1>
        {empty && <p className="text-muted-foreground mt-2 text-sm">{t("quick_add")}</p>}
      </div>

      {empty && <Onboarding />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard index={0} icon={Wallet} label={t("monthly_income")} value={fmtCurrency(data.monthly_income)} accent="#10B981" />
        <KpiCard index={1} icon={TrendingUp} label={t("net_monthly")} value={fmtCurrency(data.net_monthly)} accent="#3B82F6" />
        <KpiCard index={2} icon={Building2} label={t("portfolio_value")} value={fmtCurrency(data.portfolio_value)} accent="#F59E0B" />
        <KpiCard index={3} icon={PercentCircle} label={t("net_yield")} value={fmtPct(data.net_yield)} accent="#8B5CF6" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 rounded-sm border-border">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-6">{t("income_vs_expenses")}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.income_evolution}>
              <defs>
                <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} width={40} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                formatter={(v) => fmtCurrency(v)}
              />
              <Area type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} fill="url(#inc)" />
              <Area type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} fill="url(#exp)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 rounded-sm border-border">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-6">{t("expense_breakdown")}</h3>
          {data.expense_breakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-20">{t("no_data")}</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.expense_breakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {data.expense_breakdown.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                  formatter={(v) => fmtCurrency(v)}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard index={0} icon={Building2} label={t("properties")} value={data.properties_count} accent="#10B981" />
        <KpiCard index={1} icon={Users} label={t("tenants")} value={data.tenants_count} accent="#3B82F6" />
        <KpiCard index={2} icon={Home} label={t("occupancy")} value={fmtPct(data.occupancy)} accent="#F59E0B" />
        <KpiCard index={3} icon={PercentCircle} label={t("gross_yield")} value={fmtPct(data.gross_yield)} accent="#8B5CF6" />
      </div>
    </div>
  );
}
